class IHProductGallery {
  constructor(element) {
    this.gallery = element;
    this.track = this.gallery.querySelector('.ih-gallery__track');
    this.slides = this.gallery.querySelectorAll('.ih-gallery__slide');
    this.thumbnails = this.gallery.querySelectorAll('.ih-gallery__thumbnail');
    this.navPrev = this.gallery.querySelector('.ih-gallery__nav--prev');
    this.navNext = this.gallery.querySelector('.ih-gallery__nav--next');
    
    this.currentIndex = this.getCurrentActiveIndex();
    this.totalSlides = this.slides.length;
    this.isAnimating = false;
    
    // Touch/swipe properties
    this.startX = 0;
    this.startY = 0;
    this.currentX = 0;
    this.currentY = 0;
    this.isDragging = false;
    this.threshold = 50; // Minimum distance to trigger swipe
    
    this.init();
  }
  
  init() {
    if (this.totalSlides <= 1) return;
    
    this.bindEvents();
    this.loadVariantMediaMap();
    this.initializeVideos();
  }
  
  getCurrentActiveIndex() {
    const activeSlide = this.gallery.querySelector('.ih-gallery__slide--active');
    return activeSlide ? parseInt(activeSlide.dataset.slideIndex) : 0;
  }
  
  bindEvents() {
    // Navigation arrows
    if (this.navPrev) {
      this.navPrev.addEventListener('click', () => this.goToPrevious());
    }
    if (this.navNext) {
      this.navNext.addEventListener('click', () => this.goToNext());
    }
    
    // Thumbnail clicks
    this.thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', (e) => {
        const index = parseInt(e.currentTarget.dataset.slideIndex);
        this.goToSlide(index);
      });
    });
    
    // Touch events for swipe
    this.track.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
    this.track.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: true });
    this.track.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });
    
    // Mouse events for desktop drag
    this.track.addEventListener('mousedown', (e) => this.handleMouseDown(e));
    this.track.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    this.track.addEventListener('mouseup', (e) => this.handleMouseUp(e));
    this.track.addEventListener('mouseleave', (e) => this.handleMouseUp(e));
    
    // Keyboard navigation
    this.gallery.addEventListener('keydown', (e) => this.handleKeydown(e));
    
    // Subscribe to variant change events using the pub/sub system
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        this.handleVariantChangeFromPubSub(event);
      });
    }
    
    // Also listen for form changes as a fallback
    document.addEventListener('change', (e) => {
      if (e.target.name && e.target.name.includes('options[')) {
        this.handleVariantChangeFromForm();
      }
    });
  }
  
  loadVariantMediaMap() {
    const sectionId = this.gallery.dataset.sectionId;
    const mapElement = document.getElementById(`ih-variant-media-map-${sectionId}`);
    if (mapElement) {
      try {
        this.variantMediaMap = JSON.parse(mapElement.textContent);
      } catch (e) {
        console.warn('Could not parse variant media map:', e);
        this.variantMediaMap = {};
      }
    }
  }
  
  handleVariantChangeFromPubSub(event) {
    console.log('Variant change event received from pub/sub:', event);
    
    if (!this.variantMediaMap) {
      console.log('No variant media map available');
      return;
    }
    
    const variantId = event.data?.variant?.id;
    console.log('Variant ID:', variantId);
    
    if (!variantId) return;
    
    const mediaId = this.variantMediaMap[variantId];
    console.log('Media ID for variant:', mediaId);
    
    if (!mediaId) return;
    
    // Find slide with matching media ID
    const targetSlide = this.gallery.querySelector(`[data-media-id="${mediaId}"]`);
    console.log('Target slide found:', targetSlide);
    
    if (targetSlide) {
      const index = parseInt(targetSlide.dataset.slideIndex);
      console.log('Switching to slide index:', index);
      this.goToSlide(index);
    }
  }
  
  handleVariantChange(event) {
    console.log('Variant change event received:', event);
    
    if (!this.variantMediaMap) {
      console.log('No variant media map available');
      return;
    }
    
    const variantId = event.detail?.variant?.id;
    console.log('Variant ID:', variantId);
    
    if (!variantId) return;
    
    const mediaId = this.variantMediaMap[variantId];
    console.log('Media ID for variant:', mediaId);
    
    if (!mediaId) return;
    
    // Find slide with matching media ID
    const targetSlide = this.gallery.querySelector(`[data-media-id="${mediaId}"]`);
    console.log('Target slide found:', targetSlide);
    
    if (targetSlide) {
      const index = parseInt(targetSlide.dataset.slideIndex);
      console.log('Switching to slide index:', index);
      this.goToSlide(index);
    }
  }
  
  handleVariantChangeFromForm() {
    // Get currently selected variant from form
    const variantId = document.querySelector('[name="id"]')?.value;
    console.log('Form variant ID:', variantId);
    
    if (!variantId || !this.variantMediaMap) return;
    
    const mediaId = this.variantMediaMap[variantId];
    if (!mediaId) return;
    
    const targetSlide = this.gallery.querySelector(`[data-media-id="${mediaId}"]`);
    if (targetSlide) {
      const index = parseInt(targetSlide.dataset.slideIndex);
      this.goToSlide(index);
    }
  }
  
  // Touch event handlers
  handleTouchStart(e) {
    // Don't start dragging if touching video controls or play button
    if (e.target.closest('.ih-gallery__video-play-btn') || 
        e.target.closest('video')) {
      return;
    }
    
    this.startX = e.touches[0].clientX;
    this.startY = e.touches[0].clientY;
    this.isDragging = true;
  }
  
  handleTouchMove(e) {
    if (!this.isDragging) return;
    
    this.currentX = e.touches[0].clientX;
    this.currentY = e.touches[0].clientY;
  }
  
  handleTouchEnd(e) {
    if (!this.isDragging) return;
    
    const deltaX = this.startX - this.currentX;
    const deltaY = this.startY - this.currentY;
    
    // Check if horizontal swipe is more significant than vertical
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > this.threshold) {
      if (deltaX > 0) {
        this.goToNext();
      } else {
        this.goToPrevious();
      }
    }
    
    this.isDragging = false;
  }
  
  // Mouse event handlers (for desktop)
  handleMouseDown(e) {
    // Don't start dragging if clicking on video controls or play button
    if (e.target.closest('.ih-gallery__video-play-btn') || 
        e.target.closest('video')) {
      return;
    }
    
    this.startX = e.clientX;
    this.isDragging = true;
    this.track.style.cursor = 'grabbing';
  }
  
  handleMouseMove(e) {
    if (!this.isDragging) return;
    this.currentX = e.clientX;
  }
  
  handleMouseUp(e) {
    if (!this.isDragging) return;
    
    const deltaX = this.startX - this.currentX;
    
    if (Math.abs(deltaX) > this.threshold) {
      if (deltaX > 0) {
        this.goToNext();
      } else {
        this.goToPrevious();
      }
    }
    
    this.isDragging = false;
    this.track.style.cursor = '';
  }
  
  handleKeydown(e) {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.goToPrevious();
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.goToNext();
        break;
    }
  }
  
  goToSlide(index) {
    if (this.isAnimating || index < 0 || index >= this.totalSlides) {
      return;
    }
    
    // Allow same index to force refresh
    this.isAnimating = true;
    
    // Pause any playing videos when switching slides
    const currentSlide = this.slides[this.currentIndex];
    if (currentSlide) {
      const video = currentSlide.querySelector('video');
      if (video && !video.paused) {
        video.pause();
      }
    }
    
    // Remove active class from all slides and thumbnails
    this.slides.forEach(slide => slide.classList.remove('ih-gallery__slide--active'));
    this.thumbnails.forEach(thumb => thumb.classList.remove('ih-gallery__thumbnail--active'));
    
    // Add active class to current slide and thumbnail
    this.slides[index].classList.add('ih-gallery__slide--active');
    if (this.thumbnails[index]) {
      this.thumbnails[index].classList.add('ih-gallery__thumbnail--active');
      // Scroll thumbnail into view
      this.scrollThumbnailIntoView(index);
    }
    
    this.currentIndex = index;
    
    // Reset animation flag after transition
    setTimeout(() => {
      this.isAnimating = false;
    }, 300);
  }
  
  goToNext() {
    const nextIndex = this.currentIndex === this.totalSlides - 1 ? 0 : this.currentIndex + 1;
    this.goToSlide(nextIndex);
  }
  
  goToPrevious() {
    const prevIndex = this.currentIndex === 0 ? this.totalSlides - 1 : this.currentIndex - 1;
    this.goToSlide(prevIndex);
  }
  
  scrollThumbnailIntoView(index) {
    const thumbnail = this.thumbnails[index];
    const container = thumbnail.parentElement;
    
    if (thumbnail && container) {
      const containerRect = container.getBoundingClientRect();
      const thumbnailRect = thumbnail.getBoundingClientRect();
      
      if (thumbnailRect.left < containerRect.left) {
        container.scrollLeft -= (containerRect.left - thumbnailRect.left) + 20;
      } else if (thumbnailRect.right > containerRect.right) {
        container.scrollLeft += (thumbnailRect.right - containerRect.right) + 20;
      }
    }
  }
  
  initializeVideos() {
    // Find all video containers in the gallery
    const videoContainers = this.gallery.querySelectorAll('.ih-gallery__video');
    
    videoContainers.forEach(container => {
      const video = container.querySelector('video');
      const playBtn = container.querySelector('.ih-gallery__video-play-btn');
      
      if (video && playBtn) {
        // Ensure video doesn't autoplay
        video.autoplay = false;
        video.pause();
        
        // Handle play button click
        playBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          if (video.paused) {
            video.muted = false; // Unmute when user clicks play
            video.play();
            playBtn.classList.add('ih-gallery__video-play-btn--hidden');
          } else {
            video.pause();
            playBtn.classList.remove('ih-gallery__video-play-btn--hidden');
          }
        });
        
        // Show play button when video ends
        video.addEventListener('ended', () => {
          playBtn.classList.remove('ih-gallery__video-play-btn--hidden');
        });
        
        // Show play button when video is paused
        video.addEventListener('pause', () => {
          if (!video.ended) {
            playBtn.classList.remove('ih-gallery__video-play-btn--hidden');
          }
        });
        
        // Hide play button when video plays
        video.addEventListener('play', () => {
          playBtn.classList.add('ih-gallery__video-play-btn--hidden');
        });
      }
    });
  }
  
  destroy() {
    // Unsubscribe from pub/sub events
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
  }
}

// Initialize galleries when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  const galleries = document.querySelectorAll('.ih-gallery');
  galleries.forEach(gallery => new IHProductGallery(gallery));
});

// Re-initialize on Shopify section loads (theme editor)
document.addEventListener('shopify:section:load', function() {
  const galleries = document.querySelectorAll('.ih-gallery');
  galleries.forEach(gallery => new IHProductGallery(gallery));
});